import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SelectDropdown } from "./SelectDropdown";
import { BankAccountModal, BankAccountModalMode } from "./BankAccountModal";
import {
  CompanyBankAccount,
  listCompanyBankAccounts,
} from "../../api/companyBankAccounts";
import {
  ComponentType,
  SelectDropdownComponent,
  WidthMode,
} from "../../services/types/ComponentTypes";

interface BusinessBankAccountsSectionProps {
  disabled?: boolean;
  /** Selected bank account id from app_settings.bank_account_id */
  value?: string;
  onChange?: (bankAccountId: string) => void;
}

export const BusinessBankAccountsSection: React.FC<BusinessBankAccountsSectionProps> = ({
  disabled = false,
  value = "",
  onChange,
}) => {
  const [banks, setBanks] = useState<CompanyBankAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modalMode, setModalMode] = useState<BankAccountModalMode | null>(null);

  const selectedId = value || "";

  const setSelectedId = useCallback(
    (nextId: string) => {
      onChange?.(nextId);
    },
    [onChange]
  );

  const reloadList = useCallback(async (preferSelectId?: string) => {
    setLoading(true);
    setError("");
    try {
      const rows = await listCompanyBankAccounts();
      setBanks(rows);
      const preferred =
        preferSelectId && rows.some((r) => r.id === preferSelectId)
          ? preferSelectId
          : undefined;
      if (preferred) {
        onChange?.(preferred);
      } else if (value && !rows.some((r) => r.id === value)) {
        // Saved selection no longer exists — clear it
        onChange?.("");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load bank accounts.");
      setBanks([]);
    } finally {
      setLoading(false);
    }
  }, [onChange, value]);

  useEffect(() => {
    void reloadList();
    // Load once on mount; selection sync is handled via value/onChange props
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = useMemo(
    () => banks.map((b) => ({ value: b.id, label: b.account_title || b.id })),
    [banks]
  );

  const selectedBank = useMemo(
    () => banks.find((b) => b.id === selectedId) || null,
    [banks, selectedId]
  );

  const dropdownConfig: SelectDropdownComponent = useMemo(
    () => ({
      componentType: ComponentType.SELECT_DROPDOWN,
      name: "drp_company_bank",
      label: "Bank account",
      placeholder: "--Not Set--",
      options,
      style: { widthMode: WidthMode.FULL },
    }),
    [options]
  );

  const handleDropdownChange = (_name: string, value: any) => {
    setSelectedId(value == null ? "" : String(value));
  };

  const handleModalSuccess = (saved: CompanyBankAccount) => {
    setModalMode(null);
    void reloadList(saved.id);
  };

  const canEdit = Boolean(selectedId) && !disabled && !loading;

  return (
    <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
      <div className="flex flex-col gap-2 w-full max-w-xl">
        <div className="flex items-end gap-2 w-full">
          <div className="flex-1 min-w-0">
            <SelectDropdown
              config={dropdownConfig}
              value={selectedId}
              onChange={handleDropdownChange}
            />
          </div>

          <button
            type="button"
            onClick={() => setModalMode("add")}
            disabled={disabled || loading}
            className="p-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md transition-colors h-[38px] w-[38px] flex items-center justify-center shadow-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            title="Add bank account"
            aria-label="Add bank account"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>

          <button
            type="button"
            onClick={() => setModalMode("edit")}
            disabled={!canEdit}
            className="px-3 h-[38px] text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            title={selectedId ? "Edit selected bank account" : "Select a bank account to edit"}
          >
            Edit
          </button>
        </div>

        {loading && (
          <p className="text-xs text-gray-500 dark:text-gray-400 m-0">Loading bank accounts…</p>
        )}
        {error && (
          <p className="text-xs text-red-600 dark:text-red-400 m-0">{error}</p>
        )}

        {selectedBank && (
          <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
            {selectedBank.bank ? <div>Bank: {selectedBank.bank}</div> : null}
            {selectedBank.iban ? <div>IBAN: {selectedBank.iban}</div> : null}
            {selectedBank.account_number ? (
              <div>Account number: {selectedBank.account_number}</div>
            ) : null}
          </div>
        )}
      </div>

      {modalMode && (
        <BankAccountModal
          isOpen={true}
          mode={modalMode}
          recordId={modalMode === "edit" ? selectedId : undefined}
          onClose={() => setModalMode(null)}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};
